import dayjs from 'dayjs';
import { useDebounceFn } from '@vueuse/core';
import { isString } from 'lodash-es';
import type { EventFilters, QueryFilters } from '@/types/Event';

export const INITIAL_FILTERS_VALUES: EventFilters = { page: 1, size: 16 };
const DEBOUNCE_DELAY = 400;

export const useFilters = () => {
  const route = useRoute();
  const router = useRouter();

  const filters = useState<EventFilters>('event-filters', () => ({
    ...INITIAL_FILTERS_VALUES,
  }));

  function syncFromUrl() {
    const query = route.query as Partial<QueryFilters>;
    filters.value = {
      page: query.page ? Number(query.page) : INITIAL_FILTERS_VALUES.page,
      size: query.size ? Number(query.size) : INITIAL_FILTERS_VALUES.size,
      name: isString(query.name) ? query.name : undefined,
      selectedDepartements: query.selectedDepartements
        ? Array.isArray(query.selectedDepartements)
          ? query.selectedDepartements
          : [query.selectedDepartements]
        : undefined,
      duration: query.duration as EventFilters['duration'],
      startDate: query.startDate ? dayjs(query.startDate).toDate() : undefined,
      endDate: query.endDate ? dayjs(query.endDate).toDate() : undefined,
      source: query.source as EventFilters['source'],
      incompleteAddress:
        query.incompleteAddress as EventFilters['incompleteAddress'],
      type: query.type as EventFilters['type'],
    };
  }

  const filtersQuery = computed(() => ({
    ...filters.value,
    startDate: filters.value.startDate
      ? dayjs(filters.value.startDate).format('YYYY-MM-DD')
      : undefined,
    endDate: filters.value.endDate
      ? dayjs(filters.value.endDate).format('YYYY-MM-DD')
      : undefined,
  }));

  let internalNavigation = false;

  function pushToUrl() {
    const newQuery = filtersQuery.value;

    const { href } = router.resolve({ query: newQuery });
    if (href === route.fullPath) return;

    internalNavigation = true;
    router.push({ path: '', query: newQuery });
  }

  function setFilters(partial: Partial<EventFilters>) {
    filters.value = { ...INITIAL_FILTERS_VALUES, ...partial };
  }

  function resetFilters() {
    filters.value = { ...INITIAL_FILTERS_VALUES };
    pushToUrl();
  }

  const activeFiltersCount = computed(() => {
    const { page, size, ...relevant } = filters.value;
    return Object.keys(relevant).filter((key) => {
      const value = relevant[key as keyof typeof relevant];
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value) && value !== 'ALL';
    }).length;
  });

  const areFiltersEmpty = computed(() => activeFiltersCount.value === 0);

  const offset = computed(() => (filters.value.page - 1) * filters.value.size);

  function handlePageUpdate(event: { page: number; rows: number }) {
    filters.value.page = event.page + 1;
    filters.value.size = event.rows;
  }

  function initRouteSync(reloadFn: () => void) {
    syncFromUrl();

    watch(
      () => {
        const { page, size, ...rest } = filters.value;
        return JSON.stringify(rest);
      },
      () => {
        filters.value.page = 1;
      },
    );

    watch(
      filters,
      useDebounceFn(() => {
        pushToUrl();
        reloadFn();
      }, DEBOUNCE_DELAY),
      { deep: true },
    );

    watch(
      () => route.query,
      () => {
        if (internalNavigation) {
          internalNavigation = false;
          return;
        }
        syncFromUrl();
        reloadFn();
      },
    );
  }

  return {
    filters,
    filtersQuery,
    setFilters,
    resetFilters,
    initRouteSync,
    activeFiltersCount,
    areFiltersEmpty,
    offset,
    handlePageUpdate,
  };
};
